import { computed, type ComputedRef } from 'vue';
import { adminRoutes } from '@/router/admin-routes';
/*
 * The groups a reader already knows by sight take the navbar's own icon — Products, Components,
 * Samples, Orders, Suppliers, Sizes & Measurements, Brand and Seasons are the same things here as
 * they are up there, and two glyphs for one thing is one too many. The rest configure things the
 * navbar has no entry for, and carry an icon of their own.
 */
import { componentsIconScale, navIcons } from '@lib/nav-icons';
import bomIcon from '@icons/other/bom.svg';
import colorIcon from '@icons/filters-icons/color-name.svg';
import fieldIcon from '@icons/other3/table-layout.svg';
import insightIcon from '@icons/other2/graph-curve.svg';
import integrationIcon from '@icons/other2/link-chain.svg';
import internalIcon from '@icons/other2/wrench-laptop.svg';
import priceIcon from '@icons/filters-icons/prices.svg';
import seasonIcon from '@icons/filters-icons/seasons.svg';
import userIcon from '@icons/miscellaneous/employees.svg';
import { isAdminPageVisible, useAdminPageVisibility } from '@pages/admin/admin-pages/admin-page-visiblity';
import { useUser } from '@/store/user';
import { useUserConfig } from '@/store/user-config';
import { canSeeResourcedInternal } from '@lib/resourced-internal';

export interface AdminTab {
  name: string;
  title: string;
  group: string;
  /** Synonyms the page should also be findable by, e.g. 'freight' for Mode of Transport. */
  keywords: string;
}

export const allAdminTabs: AdminTab[] = adminRoutes.map((route) => ({
  name: route.name,
  title: route.meta.title,
  group: route.meta.group,
  keywords: route.meta.keywords,
}));

export interface AdminGroup {
  /** The key the routes carry in `meta.group`. */
  key: string;
  /** What the nav calls the group. The route keys are not shown to anyone. */
  label: string;
  icon: string;
  /**
   * Drawn larger than the box, for artwork that carries its own padding and would otherwise read
   * smaller than the icons beside it. The navbar corrects the same icon by the same amount.
   */
  iconScale?: number;
}

/**
 * The groups, in the order the nav lists them: what a product is, what it is made of, who makes it,
 * what it costs, and then the platform itself. A page belongs to the group of the thing it
 * configures, which is why colours are together rather than split by who uses them.
 */
export const adminGroups: AdminGroup[] = [
  { key: 'products', label: 'Products', icon: navIcons.products },
  { key: 'seasons', label: 'Seasons & Channels', icon: seasonIcon },
  { key: 'colors', label: 'Colors', icon: colorIcon },
  { key: 'measurements', label: 'Sizes & Measurements', icon: navIcons.measurements },
  { key: 'bom', label: 'BOM', icon: bomIcon },
  { key: 'components', label: 'Components & Materials', icon: navIcons.components, iconScale: componentsIconScale },
  { key: 'suppliers', label: 'Suppliers & Compliance', icon: navIcons.suppliers },
  { key: 'samples', label: 'Samples', icon: navIcons.samples },
  { key: 'orders', label: 'Orders', icon: navIcons.orders },
  { key: 'prices', label: 'Prices & Costs', icon: priceIcon },
  { key: 'fields', label: 'Fields & Data', icon: fieldIcon },
  { key: 'brand', label: 'Brand Setup', icon: navIcons.brand },
  { key: 'users', label: 'Users & Access', icon: userIcon },
  { key: 'integrations', label: 'Integrations & Export', icon: integrationIcon },
  { key: 'insights', label: 'Insights', icon: insightIcon },
  { key: 'resourced', label: 'Resourced Internal', icon: internalIcon },
];

const groupsByKey = new Map(adminGroups.map((group) => [group.key, group]));

export const adminGroupLabel = (key: string): string => groupsByKey.get(key)?.label ?? key;

/** The group a tab belongs to, for anything listing tabs outside the admin nav — the palette. */
export const adminGroupOf = (key: string): AdminGroup | undefined => groupsByKey.get(key);

/**
 * The tabs under their groups, in nav order, with empty groups left out — a group whose every page
 * is hidden for this brand should not leave a heading behind.
 */
export function groupAdminTabs(tabs: AdminTab[]): { group: AdminGroup; tabs: AdminTab[] }[] {
  return adminGroups
    .map((group) => ({ group, tabs: tabs.filter((tab) => tab.group === group.key) }))
    .filter(({ tabs: inGroup }) => inGroup.length > 0);
}

/**
 * Whether the user may see the brand's admin: given to them by the brand's user configuration, or by
 * the global Admin role, which reaches every brand's admin regardless of it.
 */
export function canSeeAdmin(): boolean {
  return Boolean(useUserConfig().settings?.admin?.visible || useUser().isAdmin);
}

/** Admin pages the current user/brand may see — shared by the admin page and the command palette. */
export function useVisibleAdminTabs(): ComputedRef<AdminTab[]> {
  return computed(() => {
    const resourcedInternal = canSeeResourcedInternal();
    return allAdminTabs.filter(
      ({ name, group }) =>
        isAdminPageVisible(useAdminPageVisibility.value, name) && (group !== 'resourced' || resourcedInternal)
    );
  });
}
